import { useEffect, useRef, useState } from "react";
import type { ConversationParentType, Message } from "@yagni/shared";
import { InlineChat } from "./InlineChat.js";
import { ConversationSidebar } from "../ui/conversation-sidebar.js";

/**
 * ConversationPanel — reusable chat surface bound to an isolated
 * polymorphic conversation (parent_type + parent_id).
 *
 * On mount it upserts a conversations row via POST /api/conversations and
 * then renders an InlineChat bound to that conversation id. Messages live
 * in the `conversations` scope and never appear in any channel history.
 *
 * Use this anywhere @yagni should be able to collaborate with a human on a
 * specific artifact — Vision Page, knowledge pages, goals, email drafting,
 * custom skill creation, etc.
 */
export interface ConversationPanelProps {
  /** Polymorphic parent type (from @yagni/shared CONVERSATION_PARENT_TYPES). */
  parentType: ConversationParentType;
  /** Parent row id the conversation is anchored to. */
  parentId: string;
  /** Optional display name used when the conversation is first created. */
  name?: string;
  /** Optional header caption. Defaults per parentType. */
  title?: string;
  /** Optional placeholder for the composer. */
  placeholder?: string;
  /** Optional className forwarded to the outer container. */
  className?: string;
  /**
   * Fires once per conversation as soon as the upsert + history load
   * resolves and `conversationId` is known to the panel. Generic lifecycle
   * hook — callers use it for side effects that need the conversation id
   * (e.g. the native feedback flow enriches `user_feedback.debug_context`
   * via POST /api/feedback/submit at this point). Fires again when the
   * parent (parentType/parentId) changes and a new conversation loads.
   *
   * The `meta.hasHumanMessages` flag lets consumers distinguish fresh
   * conversations from resumed ones — the feedback panel uses it to skip
   * re-capturing debug_context on re-opens so the original report's
   * snapshot isn't overwritten with stale state from a later session.
   */
  onConversationReady?: (
    conversationId: string,
    meta: { hasHumanMessages: boolean },
  ) => void;
  /**
   * Forwarded to the inner InlineChat. Fires the moment the user sends a
   * human message from this panel. Generic enough for any enclosing
   * surface that needs to react to "the user just typed something here"
   * without duplicating WebSocket plumbing — used by FeedbackSidePanel to
   * optimistically clear its resolved-ticket banner.
   *
   * The `text` arg carries the trimmed message body so callers that need
   * to act on the actual content (e.g. the redesigned + New flow's
   * similarity-bag fetch) don't have to subscribe to the message stream
   * themselves.
   */
  onHumanMessageSent?: (text: string) => void;
  /** When set, scrolls and flashes the matching message inside the panel. */
  focusMessageId?: string | null;
  /**
   * Render without the outer Card border and inner uppercase header.
   * Use this when the panel is hosted inside a parent that already supplies
   * its own chrome (e.g. `FeedbackSidePanel`); otherwise the two layers stack
   * into a visually redundant double-nested look.
   */
  chromeless?: boolean;
  /**
   * Optional metadata blob persisted onto the conversation row's `metadata`
   * JSONB column on upsert. The redesigned `+ New` flow uses this to carry
   * `scope_context` (inherited function/goal + similarity bag) so the
   * gateway's system-prompt augmentation can read it without a follow-up
   * round trip from the client.
   *
   * Captured in a ref on first mount and never re-sent on subsequent
   * upserts — the merge happens server-side via `metadata = metadata ||
   * EXCLUDED.metadata`, so re-posting on every parent change would
   * silently overwrite values a later writer set.
   */
  metadata?: Record<string, unknown>;
}

interface ConversationRow {
  id: string;
  customerId: string;
  parentType: ConversationParentType;
  parentId: string;
  name: string | null;
  archived: boolean;
  createdAt: string;
  updatedAt: string;
}

const DEFAULT_TITLE_BY_PARENT: Partial<Record<ConversationParentType, string>> = {
  vision_page: "Talk to @yagni about this",
  knowledge_page: "Ask @yagni to improve this page",
  goal: "Discuss this goal with @yagni",
  email_thread: "Draft with @yagni",
  custom_skill: "Describe this skill to @yagni",
  connected_service: "Describe a service to @yagni",
  builder_project: "Talk to @yagni about this build",
  meeting_brief: "Discuss this brief with @yagni",
  work_item: "Talk to @yagni about this ticket",
  feedback: "Help & feedback",
};

const DEFAULT_PLACEHOLDER_BY_PARENT: Partial<Record<ConversationParentType, string>> = {
  vision_page: "Just talk — I'll polish it.",
  knowledge_page: "Ask me to revise or rewrite.",
  goal: "Ask about progress, risks, or edits…",
  email_thread: "Tell me how you want to reply.",
  custom_skill: "Describe what this skill should do.",
  connected_service:
    "e.g. I want to track bugs in Linear — don't paste API keys here",
  feedback: "Describe what happened, or ask for help…",
};

export function ConversationPanel({
  parentType,
  parentId,
  name,
  title,
  placeholder,
  className,
  onConversationReady,
  onHumanMessageSent,
  focusMessageId,
  chromeless = false,
  metadata,
}: ConversationPanelProps) {
  const [conversation, setConversation] = useState<ConversationRow | null>(null);
  const [initialMessages, setInitialMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Keep the latest onConversationReady in a ref so the effect below
  // doesn't need it as a dependency. Firing is driven by parentType/
  // parentId changes, not by callback identity — re-creating the lambda
  // on every render must not cause the effect to re-run and re-upsert.
  const onConversationReadyRef = useRef(onConversationReady);
  onConversationReadyRef.current = onConversationReady;

  // Capture the latest name in a ref so the effect below doesn't need it as
  // a dependency. The conversation row's name is set on first creation only;
  // upserting with a changed name is a no-op (the route trims it but the
  // upsert returns the existing row), and re-effecting on name changes would
  // cause unnecessary re-fetches when parents stringify a fresh title each
  // render.
  const nameRef = useRef<string | undefined>(name);
  nameRef.current = name;

  // Metadata is captured once and forwarded to the upsert. Same rationale as
  // the name ref above — the server-side `metadata = metadata ||
  // EXCLUDED.metadata` merge means re-posting the same payload on every
  // parent change is harmless, but resetting the ref on each render would
  // cause the effect to re-run when callers stringify a fresh object.
  const metadataRef = useRef<Record<string, unknown> | undefined>(metadata);
  metadataRef.current = metadata;

  // Reset local state whenever the parent changes so stale messages from a
  // previous artifact don't flash before the new conversation loads. Use a
  // cancellation flag to ignore in-flight responses for parents the user has
  // already navigated away from — without this, switching artifacts quickly
  // (e.g., clicking through goals) can race the upsert response in,
  // showing the wrong conversation in the panel.
  useEffect(() => {
    let cancelled = false;
    setConversation(null);
    setInitialMessages([]);
    setLoading(true);
    setError(null);

    (async () => {
      try {
        // Upsert — the POST handler runs ON CONFLICT DO UPDATE so repeat
        // mounts return the same row without creating duplicates.
        const meta = metadataRef.current;
        const res = await fetch("/api/conversations", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            parentType,
            parentId,
            name: nameRef.current ?? null,
            ...(meta && Object.keys(meta).length > 0
              ? { metadata: meta }
              : {}),
          }),
        });
        if (cancelled) return;
        if (!res.ok) {
          throw new Error(`Failed to open conversation (${res.status})`);
        }
        const conv = (await res.json()) as ConversationRow;
        if (cancelled) return;

        // Fetch existing messages (empty on first create).
        const msgRes = await fetch(`/api/conversations/${conv.id}/messages`, {
          credentials: "include",
        });
        if (cancelled) return;
        const msgs = msgRes.ok ? ((await msgRes.json()) as Message[]) : [];
        if (cancelled) return;

        setConversation(conv);
        setInitialMessages(msgs);
        onConversationReadyRef.current?.(conv.id, {
          hasHumanMessages: msgs.some((m) => m.senderType === "human"),
        });
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : "Unknown error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [parentType, parentId]);

  const headerTitle = title ?? DEFAULT_TITLE_BY_PARENT[parentType] ?? "Talk to @yagni";
  const composerPlaceholder =
    placeholder ?? DEFAULT_PLACEHOLDER_BY_PARENT[parentType] ?? "Message @yagni…";

  if (loading && !conversation) {
    return (
      <div
        className={`p-4 text-sm ${className ?? ""}`}
        style={{ color: "var(--text-secondary)" }}
      >
        Loading conversation…
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`p-4 text-sm ${className ?? ""}`}
        style={{ color: "var(--error)" }}
      >
        Couldn't open the conversation: {error}
      </div>
    );
  }

  if (!conversation) {
    return null;
  }

  if (chromeless) {
    return (
      <div
        className={`flex h-full min-h-0 min-w-0 w-full flex-col overflow-hidden ${className ?? ""}`}
      >
        <InlineChat
          conversationId={conversation.id}
          agentName="yagni"
          initialMessages={initialMessages}
          placeholder={composerPlaceholder}
          onHumanMessageSent={onHumanMessageSent}
          focusMessageId={focusMessageId}
        />
      </div>
    );
  }

  return (
    <ConversationSidebar
      title={headerTitle}
      tone="sidebar"
      className={className}
    >
      <InlineChat
        conversationId={conversation.id}
        agentName="yagni"
        initialMessages={initialMessages}
        placeholder={composerPlaceholder}
        onHumanMessageSent={onHumanMessageSent}
        focusMessageId={focusMessageId}
      />
    </ConversationSidebar>
  );
}
